import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiUser, FiPackage } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { formatUKPhoneNumber } from '../utils/phoneFormat';
import { ordersAPI } from '../api/orders';
import { getErrorDetail } from '../utils/errorDetail';

const RegisterContainer = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  margin-top: 3rem;

  @media (min-width: 768px) {
    margin-top: 4rem;
  }
`;

const RegisterCard = styled.div`
  background: var(--white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
`;

const Title = styled.h1`
  text-align: center;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--primary-color);
    font-size: 1rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--gray-medium);
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
  }

  &:invalid {
    border-color: var(--danger-color, #f44336);
  }
`;

const Button = styled.button`
  padding: 0.875rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  color: var(--gray-dark);
  font-size: 0.95rem;

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordHint = styled.p`
  font-size: 0.8rem;
  color: var(--gray-dark);
  margin-top: 0.25rem;
`;

const OrderLinkInfo = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  div {
    flex: 1;

    h4 {
      margin: 0 0 0.25rem 0;
      font-size: 1rem;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
    }
  }
`;

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const emailFromOrder = searchParams.get('email');

  const [formData, setFormData] = useState({
    email: emailFromOrder || '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
  };

  const handlePhoneBlur = () => {
    // Format phone number when user leaves the field
    if (formData.phone) {
      const formatted = formatUKPhoneNumber(formData.phone);
      setFormData(prev => ({
        ...prev,
        phone: formatted
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordMismatchError'));
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setError(t('register.passwordTooShortError'));
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registrationData } = formData;
      await authAPI.register(registrationData);
      setSuccess(t('register.successMessage'));

      // Auto-login after successful registration
      setTimeout(async () => {
        try {
          await authAPI.login(formData.email, formData.password);

          // If there's an orderId, try to link it to the new account
          if (orderId) {
            try {
              await ordersAPI.linkOrderToAccount(parseInt(orderId));
              setSuccess(t('register.successWithOrderMessage'));
            } catch (linkErr: any) {
              // Silently handle linking errors - order might already be linked
              console.log('Order linking result:', linkErr.response?.data?.detail || 'Success');
            }
            navigate('/account');
          } else {
            navigate('/');
          }
        } catch (loginErr) {
          navigate('/login');
        }
      }, 1500);
    } catch (err: any) {
      setError(getErrorDetail(err) || t('register.defaultError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Title>{t('register.title')}</Title>
        <Subtitle>
          {orderId
            ? t('register.subtitleWithOrder')
            : t('register.subtitleDefault')}
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          {orderId && (
            <OrderLinkInfo>
              <FiPackage />
              <div>
                <h4>{t('register.linkOrderTitle')}</h4>
                <p>{t('register.linkOrderDescription')}</p>
              </div>
            </OrderLinkInfo>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormRow>
            <FormGroup>
              <Label>
                <FiUser />
                {t('register.firstNameLabel')}
              </Label>
              <Input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder={t('register.firstNamePlaceholder')}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiUser />
                {t('register.lastNameLabel')}
              </Label>
              <Input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder={t('register.lastNamePlaceholder')}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>
              <span>📧</span>
              {t('register.emailLabel')}
            </Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('register.emailPlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <span>📱</span>
              {t('register.phoneLabel')}
            </Label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handlePhoneBlur}
              placeholder={t('register.phonePlaceholder')}
              maxLength={20}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <span>🔒</span>
              {t('register.passwordLabel')}
            </Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('register.passwordPlaceholder')}
              minLength={8}
            />
            <PasswordHint>{t('register.passwordHint')}</PasswordHint>
          </FormGroup>

          <FormGroup>
            <Label>
              <span>🔒</span>
              {t('register.confirmPasswordLabel')}
            </Label>
            <Input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder={t('register.confirmPasswordPlaceholder')}
            />
          </FormGroup>

          <Button type="submit" disabled={loading}>
            {loading ? t('register.creatingAccountButton') : t('register.createAccountButton')}
          </Button>
        </Form>

        <LinkText>
          {t('register.hasAccountPrompt')} <Link to="/login">{t('register.loginLink')}</Link>
        </LinkText>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage;